import type { ReactElement } from 'react';

import { MarketplacePropertyCategory_ListingMediaState as ListingMediaState } from '@opendoor/observability/pbs/journey/data/categories/marketplace_property_category_pb';
import { formatStreetAddress } from 'components/marketplace/helpers/addressHelpers';
import type { MarketplaceProperty } from 'components/marketplace/property/types';

import { HeroTour3d } from './HeroTour3d';
import { HeroWalkthroughVideo } from './HeroWalkthroughVideo';
import type { ListingMediaAvailability } from './mediaSlot';
import { NO_ENHANCED_MEDIA } from './mediaSlot';
import type { PdpMediaSlot } from './usePdpMediaSlot';

/**
 * Reel to play in the listing's hero, or null when it has none.
 *
 * Reels are ordered newest first and bound to the listing rather than the
 * address, so a relist never resurfaces a previous ownership cycle's footage.
 */
export const heroWalkthroughVideoUrl = (
  property: Pick<MarketplaceProperty, 'walkthroughVideos'>,
): string | null => property.walkthroughVideos[0]?.url ?? null;

/**
 * Availability is derived from the URL, not reported beside it, so a listing
 * cannot enroll in the video test with nothing to play and credit the video
 * arm with a photo viewer's behaviour.
 */
export const pdpMediaAvailability = (
  videoUrl: string | null,
  tour3d: MarketplaceProperty['tour3d'],
): ListingMediaAvailability => ({
  ...NO_ENHANCED_MEDIA,
  // Truthiness, matching the renderer's own gate: an empty URL would otherwise
  // enroll a listing that paints no video.
  hasWalkthroughVideo: Boolean(videoUrl),
  hasTour3d: Boolean(tour3d?.url),
});

/**
 * Whether the hero overlay should be mounted at all.
 *
 * The gallery surrenders the hero tile's click to whatever it is handed, and
 * cannot see that the overlay renders nothing for a control-arm viewer. Mounting
 * on the URL alone therefore strips that tile's lightbox click with nothing put
 * in its place, depressing the arm the readout compares against.
 */
export const shouldMountHeroVideo = (
  videoUrl: string | null,
  assignedState: ListingMediaState,
): boolean => Boolean(videoUrl) && assignedState === ListingMediaState.VIDEO;

export const shouldMountHeroTour3d = (
  tour3d: MarketplaceProperty['tour3d'],
  assignedState: ListingMediaState,
): boolean => Boolean(tour3d?.url) && assignedState === ListingMediaState.TOUR_3D;

export const pdpHeroMedia = (
  property: Pick<
    MarketplaceProperty,
    'walkthroughVideos' | 'tour3d' | 'addressToken' | 'street' | 'unitType' | 'unitNumber'
  >,
  slot: PdpMediaSlot,
  trigger: 'fill' | 'control',
  reelWidth?: number,
): ReactElement | undefined => {
  const videoUrl = heroWalkthroughVideoUrl(property);
  const addressLabel = formatStreetAddress(property.street, property.unitType, property.unitNumber);
  if (shouldMountHeroVideo(videoUrl, slot.assignedState) && videoUrl) {
    return (
      <HeroWalkthroughVideo
        key={property.addressToken}
        videoUrl={videoUrl}
        addressToken={property.addressToken}
        addressLabel={addressLabel}
        slot={slot}
        trigger={trigger}
        reelWidth={reelWidth}
      />
    );
  }
  const tour3d = property.tour3d;
  if (shouldMountHeroTour3d(tour3d, slot.assignedState) && tour3d) {
    return (
      <HeroTour3d
        key={property.addressToken}
        tourUrl={tour3d.url}
        addressLabel={addressLabel}
        slot={slot}
        trigger={trigger}
      />
    );
  }
  return undefined;
};
